import { Droplets, ExternalLink, Flame, Info, PlugZap } from 'lucide-react';
import type { ReactNode } from 'react';
import { locale, tx } from '../i18n';
import { formatDate } from '../lib/format';
import type { HomeServices } from '../types/api';
import ReportSection from './report/ReportSection';

interface HomeServicesSectionProps {
  services: HomeServices;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

/** Electricity meter types, as the API names them from the EPC's RdSAP codes. */
function meterLabel(meter: string): string {
  const names: Record<string, [string, string, string, string]> = {
    'Dual rate (e.g. Economy 7)': [
      'Çift tarifeli (ör. Economy 7)', 'Dwutaryfowy (np. Economy 7)',
      'Bi-horário (ex. Economy 7)', 'Doble tarifa (p. ej. Economy 7)',
    ],
    'Single rate': ['Tek tarifeli', 'Jednotaryfowy', 'Tarifa simples', 'Tarifa única'],
    'Dual rate, 24 hour': [
      'Çift tarifeli, 24 saat', 'Dwutaryfowy, całodobowy', 'Bi-horário, 24 horas',
      'Doble tarifa, 24 horas',
    ],
    'Off-peak, 18 hour': [
      'Gece tarifeli, 18 saat', 'Poza szczytem, 18 godzin', 'Fora de ponta, 18 horas',
      'Valle, 18 horas',
    ],
  };
  const known = names[meter];
  return known ? tx(meter, ...known) : meter;
}

/** Utilities at the address. Informational only: not part of the score. */
export default function HomeServicesSection({ services, checkedAt }: HomeServicesSectionProps) {
  const energy = services.energy;
  const notAvailable = tx('Not available', 'Bilinmiyor',
    'Niedostępne',
    'Indisponível',
    'No disponible');

  return (
    <ReportSection
      id="section-utilities"
      icon={PlugZap}
      title={tx('Utilities', 'Altyapı hizmetleri',
    'Media',
    'Serviços',
    'Suministros')}
      note={tx(
        'Who serves this address, and how the home is heated.',
        'Bu adrese kimin hizmet verdiği ve evin nasıl ısıtıldığı.',
    'Kto obsługuje ten adres i jak ogrzewane jest mieszkanie.',
    'Quem serve esta morada e como a casa é aquecida.',
    'Quién suministra a esta dirección y cómo se calienta la vivienda.',
      )}
      informational
      quality="estimated"
      source={{
        name: tx(
          'Water and electricity network areas, with the heating record from the EPC',
          'Su ve elektrik şebeke bölgeleri, ısıtma bilgisi EPC kaydından',
          'Obszary sieci wodociągowej i energetycznej, dane o ogrzewaniu ze świadectwa EPC',
          'Áreas das redes de água e eletricidade, com o registo de aquecimento do certificado energético',
          'Áreas de las redes de agua y electricidad, con el dato de calefacción del certificado energético',
        ),
        published: services.energy?.recordedOn ?? null,
        checked: checkedAt,
      }}
    >

      <div className="grid utilities-grid">
        <div className="card stack stack--tight">
          <h3 className="label">{tx('Networks', 'Şebekeler',
    'Sieci',
    'Redes',
    'Redes')}</h3>
          <dl className="facts-list">
            <Fact icon={<Droplets size={14} aria-hidden="true" />} term={tx('Water', 'Su',
    'Woda',
    'Água',
    'Agua')}>
              {services.water ?? notAvailable}
              {services.sewerage && services.sewerage !== services.water ? (
                <span className="muted">
                  {' '}
                  ({tx('wastewater', 'atık su',
    'ścieki',
    'águas residuais',
    'aguas residuales')}: {services.sewerage})
                </span>
              ) : null}
            </Fact>
            <Fact icon={<PlugZap size={14} aria-hidden="true" />} term={tx('Power network', 'Elektrik şebekesi',
    'Sieć elektryczna',
    'Rede elétrica',
    'Red eléctrica')}>
              {services.electricityNetwork ?? notAvailable}
              <span className="muted">
                {' '}
                &middot; {tx('power cut: call', 'elektrik kesintisi:',
    'awaria prądu: dzwoń',
    'avaria elétrica: ligue',
    'avería eléctrica: llama al')} {services.powerCutNumber}
              </span>
            </Fact>
            <Fact icon={<Flame size={14} aria-hidden="true" />} term={tx('Gas emergency', 'Doğalgaz acil',
    'Pogotowie gazowe',
    'Emergência de gás',
    'Urgencias de gas')}>
              {services.gasEmergencyNumber}
            </Fact>
          </dl>
          <p className="tiny muted">{services.supplierNote}</p>
          <div className="row" style={{ gap: 14 }}>
            <a className="tiny" href={services.supplierLookupUrl} target="_blank" rel="noreferrer">
              {tx('Find the gas supplier', 'Gaz tedarikçisini bul',
    'Znajdź dostawcę gazu',
    'Encontrar o fornecedor de gás',
    'Buscar el suministrador de gas')} <ExternalLink size={11} aria-hidden="true" />
            </a>
            <a className="tiny" href={services.networkLookupUrl} target="_blank" rel="noreferrer">
              {tx('Check the network operator', 'Şebeke işletmecisini kontrol et',
    'Sprawdź operatora sieci',
    'Confirmar o operador da rede',
    'Comprobar el operador de la red')}{' '}
              <ExternalLink size={11} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="card stack stack--tight">
          <h3 className="label">{tx('Heating and meters', 'Isıtma ve sayaçlar',
    'Ogrzewanie i liczniki',
    'Aquecimento e contadores',
    'Calefacción y contadores')}</h3>
          {energy ? (
            <>
              <dl className="facts-list">
                <Fact term={tx('Heating', 'Isıtma',
    'Ogrzewanie',
    'Aquecimento',
    'Calefacción')}>{energy.mainHeating ?? tx('Not recorded', 'Kayıtlı değil',
    'Nie odnotowano',
    'Não registado',
    'No consta')}</Fact>
                {energy.heatingControls ? (
                  <Fact term={tx('Controls', 'Kontroller',
    'Sterowanie',
    'Comandos',
    'Controles')}>{energy.heatingControls}</Fact>
                ) : null}
                {energy.hotWater ? <Fact term={tx('Hot water', 'Sıcak su',
    'Ciepła woda',
    'Água quente',
    'Agua caliente')}>{energy.hotWater}</Fact> : null}
                <Fact term={tx('Mains gas', 'Şebeke gazı',
    'Gaz sieciowy',
    'Gás de rede',
    'Gas de red')}>{yesNo(energy.mainsGas)}</Fact>
                {energy.electricityMeter ? (
                  <Fact term={tx('Electricity meter', 'Elektrik sayacı',
    'Licznik energii',
    'Contador de eletricidade',
    'Contador de electricidad')}>{meterLabel(energy.electricityMeter)}</Fact>
                ) : null}
                {energy.electricitySmartMeter !== null || energy.gasSmartMeter !== null ? (
                  <Fact term={tx('Smart meters', 'Akıllı sayaçlar',
    'Liczniki inteligentne',
    'Contadores inteligentes',
    'Contadores inteligentes')}>
                    {tx('electricity', 'elektrik',
    'prąd',
    'eletricidade',
    'electricidad')} {yesNo(energy.electricitySmartMeter).toLocaleLowerCase(locale())},{' '}
                    {tx('gas', 'gaz',
    'gaz',
    'gás',
    'gas')} {yesNo(energy.gasSmartMeter).toLocaleLowerCase(locale())}
                  </Fact>
                ) : null}
              </dl>
              {services.remarks.length > 0 ? (
                <ul className="remarks">
                  {services.remarks.map((remark) => (
                    <li key={remark}>
                      <Info size={13} aria-hidden="true" />
                      <span>{remark}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <p className="tiny muted">
                {energy.recordedOn
                  ? tx(
                      `From the Energy Performance Certificate, assessed ${formatDate(energy.recordedOn)}. Things may have changed since.`,
                      `Enerji Performans Sertifikasından, değerlendirme tarihi ${formatDate(energy.recordedOn)}. O tarihten bu yana değişmiş olabilir.`,
    `Ze świadectwa charakterystyki energetycznej, ocena z ${formatDate(energy.recordedOn)}. Od tego czasu mogło się coś zmienić.`,
    `Do certificado energético, avaliado em ${formatDate(energy.recordedOn)}. Pode ter mudado entretanto.`,
    `Del certificado energético, evaluado el ${formatDate(energy.recordedOn)}. Puede haber cambiado desde entonces.`,
                    )
                  : tx(
                      'From the Energy Performance Certificate. Things may have changed since.',
                      'Enerji Performans Sertifikasından. O tarihten bu yana değişmiş olabilir.',
    'Ze świadectwa charakterystyki energetycznej. Od tego czasu mogło się coś zmienić.',
    'Do certificado energético. Pode ter mudado entretanto.',
    'Del certificado energético. Puede haber cambiado desde entonces.',
                    )}
              </p>
            </>
          ) : (
            <p className="small muted">
              {tx(
                'No Energy Performance Certificate detail for this home.',
                'Bu konut için Enerji Performans Sertifikası ayrıntısı yok.',
    'Brak szczegółów świadectwa energetycznego dla tego mieszkania.',
    'Sem detalhes do certificado energético para esta casa.',
    'No hay detalles del certificado energético para esta vivienda.',
              )}
            </p>
          )}
        </div>
      </div>
    </ReportSection>
  );
}

function Fact({ icon, term, children }: { icon?: ReactNode; term: string; children: ReactNode }) {
  return (
    <div className="facts-list__row">
      <dt>
        {icon}
        {term}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

function yesNo(value: boolean | null): string {
  if (value === null) {
    return tx('Not recorded', 'Kayıtlı değil',
    'Nie odnotowano',
    'Não registado',
    'No consta');
  }
  return value ? tx('Yes', 'Evet',
    'Tak',
    'Sim',
    'Sí') : tx('No', 'Hayır',
    'Nie',
    'Não',
    'No');
}
